import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createApiClient } from 'api/apiClient';

import {
  Alert,
  AlertTitle,
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import I18nKey from 'lib/I18nKeys';
import {
  GetUserListResponse,
  IMEIDetailsResponse,
  UserDataResponse,
} from 'api/SharedTypes';
import { EMPTY_IMEI_RESPONSE_DATA } from 'services/Utils';
import { isArray, sortedUniq } from 'lodash';
import { useMutation, useQuery } from 'react-query';
import { FormFieldType } from 'services/Utils';
import { FormFieldObject } from 'components/UserForm';
import SelectField from 'components/UserFormFields/SelectField';
import ButtonGroupField from 'components/UserFormFields/ButtonGroupField';
import { UserContext, UserContextState } from 'contexts/userContext';
import { AutomationDialog } from 'components/AutomationDialog/AutomationDialog';

interface DevicePairingProps {
  openDevicePairing: boolean;
  userData: UserDataResponse;
  handleDevicePairing: () => void;
  reloadUserData: (id: number) => void;
  readonly handleRemoteMonitoring?: (
    patientNetwork: GetUserListResponse[],
  ) => void;
  readonly patientNetwork?: GetUserListResponse[];
  handleClose: (flag: boolean) => void;
  setOpenUnpairDialog?: (flag: boolean) => void;
  openUnpairDialog?: boolean | undefined;
}

const PAIRING_DATE_FORMAT = 'yyyy-MM-dd hh:mm:ss';
const DevicePairing: React.FC<DevicePairingProps> = ({
  openDevicePairing,
  userData,
  handleDevicePairing,
  reloadUserData,
  handleRemoteMonitoring,
  patientNetwork,
  handleClose,
  setOpenUnpairDialog,
  openUnpairDialog,
}) => {
  const { setUpdateClick } = useContext<UserContextState>(UserContext);
  const intl = useIntl();

  const accessToken = localStorage.getItem('access_token');
  const apiClient = createApiClient(accessToken!, new AbortController());
  const [unpairDate, setUnpairDate] = useState<DateTime>(DateTime.now());
  const [pairDate, setPairDate] = useState<DateTime | null>(DateTime.now());
  const [minPairDate, setMinPairDate] = useState<DateTime | null>(
    DateTime.now(),
  );
  const [unpairDialogOpen, setUnpairDialogOpen] = useState<boolean>(false);
  const [open, setOpen] = React.useState(false);

  const [pairedDialog, setPairedDialog] = useState<boolean>(false);
  const [pairedPatient, setPairedPatient] = useState<UserDataResponse | null>(
    null,
  );
  const [value, setValue] = React.useState<IMEIDetailsResponse>(
    EMPTY_IMEI_RESPONSE_DATA,
  );
  const [inputValue, setInputValue] = React.useState<string>('');
  const [unpairedDevices, setUnpairedDevices] = useState<IMEIDetailsResponse[]>(
    [],
  );
  const [isAutomationDialogOpen, setIsAutomationDialogOpen] =
    useState<boolean>(false);

  const [selectedDeviceImei, setSelectedDeviceImei] = useState<any>('');

  const payloadBodyRef = useRef<any>();

  const { isLoading: isGettingUnpairedDevices, mutate: getUnpairedDevices } =
    useMutation(
      'get-unpaired-devices',
      (value: string) => apiClient.getUnpairedDevices(value),
      {
        onSuccess: data => {
          setUnpairedDevices(data);
        },
      },
    );

  // let userIntId: any;

  const userIntId = useRef<any>();

  useEffect(() => {
    userIntId.current = userData.internal_id?.toString();
  }, [userData.internal_id]);

  const { isLoading: isUnpairingDevice, mutate: unpairDeviceFromPatient } =
    useMutation(
      'unpair-device-from-patient',
      ({
        imei,
        unpairDate,
        userInternalId,
      }: {
        imei: string;
        unpairDate: string;
        userInternalId: string;
      }) =>
        apiClient.postUnpairPatientsDevice(imei, unpairDate, userInternalId),
      {
        onSuccess: () => {
          setOpenUnpairDialog?.(false);
          reloadUserData(userData.id!);
          setUnpairDate(DateTime.now());
        },
        onError: () => {
          setOpenUnpairDialog?.(false);
        },
      },
    );

  const { isLoading: isPairingDevice, mutate: pairDevice } = useMutation(
    'pair-device',
    ({
      imei,
      userInternalId,
      pairDate,
      device_type,
    }: {
      imei: string;
      userInternalId: string;
      pairDate: string;
      device_type: string;
    }) => apiClient.postPairDevice(imei, userInternalId, pairDate, device_type),
    {
      onSuccess: (response: any) => {
        localStorage.setItem('placeOrder', '1');
        reloadUserData(userData.id!);
        handleDevicePairing();
        handleClose(false);
      },
    },
  );

  const { isLoading: isGettingPairedUser, mutate: getPairedUser } = useMutation(
    'get-unpaired-devices',
    (imei: string) => apiClient.getPairedUser(imei),
    {
      onSuccess: response => {
        if (!response) {
          let device_type_for_pairing = selectedDeviceType;
          if (selectedDeviceProvider === 'Smart Meter') {
            if (selectedDeviceType === 'Blood Pressure' && selectedCuffType) {
              device_type_for_pairing = selectedCuffType;
            } else if (
              selectedDeviceType === 'Weighing Scale' &&
              selectedWeighingScaleType
            ) {
              device_type_for_pairing = selectedWeighingScaleType;
            }
          }
          pairDevice({
            imei: value.imei,
            userInternalId: userData.internal_id?.toString() || '',
            pairDate:
              pairDate?.toUTC().toFormat('yyyy-MM-dd HH:mm:ss').toString() ||
              '',
            device_type: device_type_for_pairing,
          });
          handleRemoteMonitoring?.(patientNetwork!);
          setTimeout(() => {
            setUpdateClick(true);
          }, 2000);
        } else {
          setPairedDialog(true);
          setPairedPatient(response);
          handleClose(false);
        }
      },
    },
  );

  const {
    isLoading: isLoadingplaceOrder,
    data: placeOrderData,
    refetch: placeOrder,
  } = useQuery(
    'place-orders',
    async () => {
      const data = await apiClient.placeOrder(payloadBodyRef.current);
      sessionStorage.setItem('ortderType', 'auto');
      setTimeout(() => {
        setUpdateClick(true);
      }, 2000);
      handleRemoteMonitoring?.(patientNetwork!);
      return data;
    },
    {
      onSuccess: (data: any) => {
        localStorage.setItem('placeOrder', '1');
        handleDevicePairing();
        handleClose(false);
      },
      enabled: false,
    },
  );

  const [deviceTypeOption, setDeviceTypeOption] = useState([
    {
      label: 'Blood Pressure Device',
      value: 'Blood Pressure',
      disabled: false,
    },
    {
      label: 'Weighing Scale Device',
      value: 'Weighing Scale',
      disabled: false,
    },
  ]);

  const [providerTypeOption, setProviderTypeOption] = useState([
    {
      label: 'BodyTrace',
      value: 'BodyTrace',
      disabled: false,
    },
    {
      label: 'Smart Meter',
      value: 'Smart Meter',
      disabled: false,
    },
  ]);

  const {
    isLoading: deviceTypeLoading,
    data: deviceTypeData,
    mutate: getDeviceType,
  } = useMutation(
    ['get-device-type'],
    async () => apiClient.getDeviceType(userData.internal_id?.toString() || ''),
    {
      onSuccess: (data: any) => {
        const updatedDeviceTypeOption = [
          {
            label: 'Blood Pressure Device',
            value: 'Blood Pressure',
            disabled: !data.some(
              (item: any) =>
                item.device_type === 'Blood Pressure' && item.valid === true,
            ),
          },
          {
            label: 'Weighing Scale Device',
            value: 'Weighing Scale',
            disabled: !data.some(
              (item: any) =>
                item.device_type === 'Weighing Scale' && item.valid === true,
            ),
          },
        ];
        const defaultDeviceType = updatedDeviceTypeOption.find(
          option => !option.disabled,
        )?.value;

        setDeviceTypeOption(updatedDeviceTypeOption);

        if (defaultDeviceType) {
          setSelectedDeviceType(defaultDeviceType);
        }
      },
      onError: (error: any) => {
        console.log('getDeviceTypeError', error);
      },
    },
  );

  useEffect(() => {
    getDeviceType();
  }, []);

  useEffect(() => {
    if (openUnpairDialog && deviceTypeData) {
      const devicesWithImei = deviceTypeData.filter(
        (item: any) => item.imei && item.imei !== '',
      );
      const options = devicesWithImei.map((item: any) => ({
        label: item.device_type,
        value: item.device_type,
        disabled: !item.imei,
      }));
      setDeviceTypeOption(options);
      if (options.length > 0) {
        setSelectedDeviceType(options[0].value);
      } else {
        setDeviceTypeOption([]);
        setSelectedDeviceType('');
      }
    }
  }, [openUnpairDialog, deviceTypeData]);

  const options = useMemo(() => {
    if (unpairedDevices) {
      if (isArray(unpairedDevices)) {
        return sortedUniq(unpairedDevices);
      }
    }
    return [];
  }, [unpairedDevices]);

  const onIMEIInputChange = (value: string) => {
    if (value.length === 6) {
      getUnpairedDevices(value);
    } else if (value.length === 15) {
      let selectedOption = options.find(item => {
        return item.imei === value;
      });

      if (selectedOption?.end_date === null) {
        setMinPairDate(DateTime.now().minus({ days: 30 }));
      } else {
        setMinPairDate(
          DateTime.fromFormat(selectedOption?.end_date!, PAIRING_DATE_FORMAT),
        );
      }
    }
  };
 
  const clearForm = () => {
    setOpen(false);
    setValue(EMPTY_IMEI_RESPONSE_DATA);
    setInputValue('');
    setPairDate(DateTime.now());
  };

  const cancelUnpairing = () => {
    setUnpairDate(DateTime.now());
    setUnpairDialogOpen(false);
  };

  const cancelPairing = () => {
    setPairedDialog(false);
    setUnpairedDevices([]);
    clearForm();
  };

  const onClickunpairDevice = () => {
    setUnpairDialogOpen(true);
  };

  const unpairDevice = () => {
    const deviceToUnpair = deviceTypeData.find(
      (d: any) => d.device_type === selectedDeviceType,
    );

    if (deviceToUnpair?.imei) {
      unpairDeviceFromPatient({
        imei: deviceToUnpair.imei,
        unpairDate:
          unpairDate.toUTC().toFormat('yyyy-MM-dd HH:mm:ss').toString() || '',
        userInternalId: userIntId.current || '',
      });
    }

    setTimeout(() => {
      setOpenUnpairDialog?.(false);
      setUpdateClick(true);
    }, 2000);
  };

  const [type, setType] = useState<string>('Manual');
  const [selectedDeviceType, setSelectedDeviceType] =
    useState<string>('Blood Pressure');

  const [selectedDeviceProvider, setSelectedDeviceProvider] = useState<string>("BodyTrace")

  const typeOptions: any = [
    {
      label: 'Automated',
      value: 'Automated',
    },
    {
      label: 'Manual',
      value: 'Manual',
    },
  ];

  const deviceType: FormFieldObject = {
    deviceType: {
      type: FormFieldType.select,
      selectOptions: deviceTypeOption,
      isRequired: true,
      value: selectedDeviceType,
      onChange: value => {
        setSelectedDeviceType(value);
      },
      label: intl.formatMessage({
        id: 'Select Device Type',
      }),
      isDisabled: !selectedDeviceProvider,
    },
  };

  const devicePairingFields: FormFieldObject = {
    deviceType: {
      type: FormFieldType.select,
      selectOptions: typeOptions,
      isRequired: true,
      value: type,
      onChange: value => {
        setType(value);
      },
      label: intl.formatMessage({
        id: I18nKey.DEVICE_ORDER_TYPE_PAIRING_TITLE,
      }),
      isDisabled: !selectedDeviceProvider,
    },
  };

  const provierType: FormFieldObject = {
    providerType: {
      type: FormFieldType.select,
      selectOptions: providerTypeOption,
      isRequired: true,
      value: selectedDeviceProvider,
      onChange: (value:any )=> {
        setSelectedDeviceProvider(value);
      },
      label: intl.formatMessage({
        id: 'Select Device Provider',
      }),
      isDisabled: false,
    },
  };

  // const upairDeviceType: FormFieldObject = {
  //   deviceType: {
  //     type: FormFieldType.select,
  //     selectOptions: deviceTypeOption,
  //     isRequired: true,
  //     value: selectedDeviceType,
  //     onChange: value => {
  //       setSelectedDeviceType(value);
  //     },
  //     label: intl.formatMessage({
  //       id: 'Select Device Type',
  //     }),
  //   },
  // };

  const callPlaceOrder = () => {
    setIsAutomationDialogOpen(true);
  };

  const onClickPairDevice = () => getPairedUser(value.imei);

  const handleAutomationDialogClose = () => setIsAutomationDialogOpen(false);

  useEffect(() => {
    if (openUnpairDialog && selectedDeviceType && deviceTypeData) {
      const device = deviceTypeData.find(
        (d: any) => d.device_type === selectedDeviceType,
      );
      if (device) {
        setSelectedDeviceImei(device.imei || '');
      } else {
        setSelectedDeviceImei('');
      }
    } else if (openUnpairDialog) {
      setSelectedDeviceImei('');
    }
  }, [selectedDeviceType, deviceTypeData, openUnpairDialog]);

  const handleAutomationAccept = () => {
    const deviceType = selectedDeviceType;
    const deviceProvider = selectedDeviceProvider;

    let finalDeviceType = deviceType;
    if (deviceProvider === 'Smart Meter') {
      if (deviceType === 'Blood Pressure') {
        finalDeviceType = selectedCuffType;
      } else if (deviceType === 'Weighing Scale') {
        finalDeviceType = selectedWeighingScaleType;
      }
    }

    if (deviceProvider === 'Smart Meter') {
      const deviceToOrder = (deviceTypeData || []).find(
        (item: any) =>
          item.provider === 'Smart Meter' &&
          item.device_type === finalDeviceType &&
          item.valid,
      );

      if (deviceToOrder) {
        const payloadBody = {
          patient_int_id: userData.internal_id || '',
          kit_id: null,
          org_id: deviceToOrder.organization_id || '',
          device_type: deviceToOrder.device_type || '',
          device_provider: deviceProvider || '',
          sku_id: deviceToOrder.sku || '',
        };
        payloadBodyRef.current = payloadBody;
        placeOrder();
      }
    } else {
      const orderDeviceData = (deviceTypeData || []).find(
        (item: any) =>
          item.device_type === finalDeviceType &&
          item.provider === deviceProvider,
      );

      if (orderDeviceData) {
        const payloadBody = {
          patient_int_id: userData.internal_id || '',
          kit_id: orderDeviceData.kit_id || '',
          org_id: orderDeviceData.organization_id || '',
          device_type: orderDeviceData.device_type || '',
          device_provider: deviceProvider || '',
          sku_id: null,
        };
        payloadBodyRef.current = payloadBody;
        placeOrder();
      }
    }

    setIsAutomationDialogOpen(false);
  };

  // Add state for selected cuff type
  const [selectedCuffType, setSelectedCuffType] = useState<string>("");
  const [selectedWeighingScaleType, setSelectedWeighingScaleType] =
    useState<string>("");

  // Compute cuff type options from deviceTypeData
  const cuffTypeOptions = (deviceTypeData || [])
    .filter((item: any) => item.provider === 'Smart Meter' && item.device_type.includes('Blood Pressure'))
    .map((item: any) => ({
      label: item.device_type,
      value: item.device_type,
      disabled: !item.valid,
    }));

  const weighingScaleTypeOptions = (deviceTypeData || [])
    .filter(
      (item: any) =>
        item.provider === 'Smart Meter' &&
        item.device_type.includes('Weighing Scale'),
    )
    .map((item: any) => ({
      label: item.device_type,
      value: item.device_type,
      disabled: !item.valid,
    }));

  return (
    <>
      <Dialog open={openDevicePairing} onClose={handleDevicePairing} fullWidth style={{width: "560px", placeSelf: "center"}}>
        {(isLoadingplaceOrder || deviceTypeLoading) && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          >
            <CircularProgress color="primary" />
          </div>
        )}
        <DialogTitle>
          {intl.formatMessage({ id: I18nKey.DEVICE_PAIRING_TITLE })}
        </DialogTitle>

        <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
          {sessionStorage.getItem('deviceAssignment') == 'Both' && (
            <>
              <br />
              <ButtonGroupField fieldItem={provierType.providerType} />
            </>
          )}
        </div>
         <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
          {sessionStorage.getItem('deviceAssignment') == 'Both' && (
            <>
              <br />
              <ButtonGroupField fieldItem={deviceType.deviceType} />
              {/* Cuff Type dropdown for Smart Meter Blood Pressure */}
              {(selectedDeviceProvider === 'Smart Meter' && selectedDeviceType === 'Blood Pressure' && cuffTypeOptions.length > 0) && (
                <div style={{ marginTop: 36 }}>
                  <SelectField
                    fieldItem={{
                      type: FormFieldType.select,
                      label: 'Select Cuff Type',
                      value: selectedCuffType,
                      onChange: setSelectedCuffType,
                      selectOptions: cuffTypeOptions,
                      isRequired: true,
                      isDisabled: false,
                    }}
                  />
                </div>
              )}
              {/* Scale Type dropdown for Smart Meter Weighing Scale */}
              {selectedDeviceProvider === 'Smart Meter' &&
                selectedDeviceType === 'Weighing Scale' &&
                weighingScaleTypeOptions.length > 0 && (
                  <div style={{ marginTop: 36 }}>
                    <SelectField
                      fieldItem={{
                        type: FormFieldType.select,
                        label: 'Select Scale Type',
                        value: selectedWeighingScaleType,
                        onChange: setSelectedWeighingScaleType,
                        selectOptions: weighingScaleTypeOptions,
                        isRequired: true,
                        isDisabled: false,
                      }}
                    />
                  </div>
                )}
            </>
          )}
        </div>
        <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
          {sessionStorage.getItem('deviceAssignment') == 'Both' && (
            <>
              <br />
              <SelectField fieldItem={devicePairingFields.deviceType} />
              <br />
              <br />
            </>
          )}
        </div>
        {type !== 'Automated' && (
          <>
            <Autocomplete
              clearOnEscape
              open={open}
              onOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              value={value}
              onChange={(event, newValue) => {
                setValue(newValue!);
                setMinPairDate(
                  newValue?.end_date
                    ? DateTime.fromFormat(
                        newValue?.end_date!,
                        PAIRING_DATE_FORMAT,
                      )
                    : DateTime.now(),
                );
              }}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
                if (newInputValue !== '' || newInputValue !== null) {
                  onIMEIInputChange(newInputValue);
                }
              }}
              noOptionsText="No Devices Available"
              getOptionLabel={option => option.imei}
              options={options}
              loading={isGettingUnpairedDevices}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                  {...params}
                  sx={{ marginLeft: 3, width: '92%' }}
                  label={<FormattedMessage id={I18nKey.DEVICE_IMEI_ENTRY} />}
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isGettingUnpairedDevices ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
            <Grid>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DatePicker
                  disableFuture
                  inputFormat="MM/dd/yyyy"
                  views={['year', 'month', 'day']}
                  maxDate={DateTime.now()}
                  minDate={minPairDate}
                  value={pairDate}
                  onChange={newValue => {
                    setPairDate(newValue);
                  }}
                  renderInput={(params: TextFieldProps) => (
                    <TextField
                      sx={{ marginLeft: 3, marginTop: 3, width: '92%' }}
                      variant="standard"
                      {...params}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </>
        )}
        {type == 'Automated' && <></>}
        <br />
        <Dialog open={pairedDialog}>
          <Alert severity="warning">
            <AlertTitle>
              <FormattedMessage id={I18nKey.WARNING} />
            </AlertTitle>
            {`This device is already paired to ${pairedPatient?.last_name} ${pairedPatient?.first_name}`}
          </Alert>
          <DialogActions sx={{ backgroundColor: '#FFF4E5' }}>
            <Button onClick={cancelPairing}>
              {intl.formatMessage({ id: I18nKey.OK })}
            </Button>
          </DialogActions>
        </Dialog>
        <DialogActions style={{ display: "flex", justifyContent: "space-between", paddingLeft: 22, paddingRight: 22, marginBottom: 15, gap: 10}}>
          <Button  style={{borderRadius: 6}} onClick={handleDevicePairing} variant='outlined' fullWidth>
            {intl.formatMessage({ id: I18nKey.CANCEL })}
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={type === 'Manual' ? onClickPairDevice : callPlaceOrder}
            sx={{ marginRight: 1 }}
            color={'primary'}
            disabled={
              deviceTypeOption.filter((item: any) => {
                return item.disabled !== true;
              }).length === 0
            }
            style={{borderRadius: 6}}
          >
            {isGettingPairedUser ? (
              <CircularProgress size={30} />
            ) : (
              intl.formatMessage({
                id: I18nKey.PLACE_ORDER,
              })
            )}
          </Button>
        </DialogActions>
        <AutomationDialog
          open={isAutomationDialogOpen}
          handleAutomationAccept={handleAutomationAccept}
          handleClose={handleAutomationDialogClose}
          patientUserData={userData}
        />
      </Dialog>
      <Dialog
        open={!!openUnpairDialog}
        onClose={() => setOpenUnpairDialog?.(false)}
        maxWidth={'sm'}
        fullWidth
      >
        {deviceTypeLoading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          >
            <CircularProgress color="primary" />
          </div>
        )}
        <DialogTitle>{`Unpair Device ${
          selectedDeviceImei ? selectedDeviceImei : 'Not Available'
        }`}</DialogTitle>
        <DialogContent>
          <Grid>
            <div
              style={{
                paddingLeft: '24px',
                paddingRight: '24px',
                marginTop: 20,
                marginBottom: 20,
              }}
            >
              <SelectField fieldItem={deviceType.deviceType} />
            </div>
            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en">
              <DatePicker
                disableFuture
                label={<FormattedMessage id={I18nKey.DEVICE_UNPAIR_DATE} />}
                inputFormat="MM/dd/yyyy"
                views={['year', 'month', 'day']}
                value={unpairDate}
                minDate={DateTime.fromFormat(
                  userData.start_date!.split(' ')[0],
                  'MM/dd/yyyy',
                )}
                maxDate={DateTime.now()}
                onChange={(newValue: DateTime | null) => {
                  setUnpairDate(newValue!);
                }}
                renderInput={(params: TextFieldProps) => (
                  <TextField
                    sx={{ marginLeft: 3, width: '92%' }}
                    variant="standard"
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={onClickunpairDevice}
            disabled={!selectedDeviceImei ? true : false}
          >
            UNPAIR DEVICE
          </Button>
          <Button onClick={() => setOpenUnpairDialog?.(false)}>CANCEL</Button>
        </DialogActions>
      </Dialog>
      {/* unpair warning modal */}
      <Dialog open={unpairDialogOpen}>
        <Alert severity="warning">
          <AlertTitle>
            <FormattedMessage id={I18nKey.WARNING} />
          </AlertTitle>
          {`This action will prevent re-pairing of this device until ${unpairDate
            .plus({ days: 1 })
            .toFormat('MM-dd-yyyy')}. Do you want to continue?`}
        </Alert>
        <DialogActions sx={{ backgroundColor: '#FFF4E5' }}>
          <Button onClick={cancelUnpairing}>
            {intl.formatMessage({ id: I18nKey.CANCEL })}
          </Button>
          <Button onClick={unpairDevice}>
            {isUnpairingDevice ? (
              <CircularProgress size={30} />
            ) : (
              intl.formatMessage({ id: I18nKey.OK })
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DevicePairing;
